import type { ZonedDateTime } from '@internationalized/date';
import { parseDuration } from '@internationalized/date';

/**
 * Converts an ISO 8601 duration string to milliseconds.
 *
 * Semantics:
 * - If `relativeTo` is provided, months/years/weeks/days are applied in calendar space
 *   using the `ZonedDateTime`'s calendar/time zone and the result is the difference
 *   in absolute milliseconds between the start and end (DST-safe).
 * - If `relativeTo` is omitted, months/years are unsupported and will throw.
 *   Weeks and days are computed arithmetically as 7×24h and 24h respectively.
 *   Hours/minutes/seconds/milliseconds are always arithmetic.
 *
 * @param value ISO 8601 duration (e.g. "PT90M", "P1M", "P1W2DT3H").
 * @param options.relativeTo Optional `ZonedDateTime` used as the reference for calendar math.
 * @returns Milliseconds represented by the duration under the given semantics.
 * @throws RangeError when months/years are present without `relativeTo`.
 */
export function durationToMilliseconds(value: string, options?: { relativeTo?: ZonedDateTime }): number {
  const d = parseDuration(value);

  const hasMonthsOrYears = Boolean(d.years || d.months);
  const hasWeeksOrDays = Boolean(d.weeks || d.days);

  // If a reference is provided, compute by calendar math to be DST-safe.
  if (options?.relativeTo) {
    const start = options.relativeTo;
    const end = start.add(d);
    return end.toDate().getTime() - start.toDate().getTime();
  }

  // Without a reference, months/years cannot be reliably converted. Weeks/days fall back to fixed lengths.
  if (hasMonthsOrYears) {
    throw new RangeError('durationToMilliseconds requires a relativeTo when duration includes years/months');
  }

  // Time-only (and optionally weeks/days) – compute arithmetically.
  const daysMs = hasWeeksOrDays ? ((d.weeks ?? 0) * 7 + (d.days ?? 0)) * 24 * 60 * 60 * 1000 : 0;
  const hoursMs = (d.hours ?? 0) * 60 * 60 * 1000;
  const minutesMs = (d.minutes ?? 0) * 60 * 1000;
  const secondsMs = (d.seconds ?? 0) * 1000;
  const millis = d.milliseconds ?? 0;
  return daysMs + hoursMs + minutesMs + secondsMs + millis;
}
